
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import type { RaceEvent, RaceCategory } from '../types';
import { ChevronDownIcon } from './icons/PlaybackIcons';

interface HeaderProps {
  race: RaceEvent;
  activeCategory: RaceCategory;
}

const formatDuration = (ms: number) => {
  const minutes = Math.round(ms / 1000 / 60);
  return `${minutes} mins`;
};

const formatStartTime = (ms: number) => {
    if (!ms || ms === 0) return 'N/A';
    return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const formatActualStartTime = (ms: number) => {
    if (!ms || ms === 0) return 'Awaiting rider data...';
    return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const Header: React.FC<HeaderProps> = ({ race, activeCategory }) => {
  const navigate = useNavigate();

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const targetCategoryId = e.target.value;
      if (targetCategoryId && targetCategoryId !== (activeCategory.slug || activeCategory.id)) {
          navigate(`/race/${race.slug || race.id}/category/${targetCategoryId}`);
      }
  };

  const showDropdown = race.categories.length > 1;
  const isGroupRide = race.eventType === 'group_ride';
  const occurrenceLabel = (() => {
      if (!race.occurrenceDate) return race.date;
      const [y, m, d] = race.occurrenceDate.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  })();

  return (
    <header className="bg-gray-800 text-white px-4 py-3 shadow-md border-b border-gray-700">
      <div className="flex items-center justify-start gap-x-4">
        <h1 className="text-2xl font-bold text-cyan-400 truncate">{race.name}</h1>
        <div className="flex items-center flex-wrap justify-start gap-x-4 gap-y-1 text-sm text-gray-400 flex-shrink-0">

          {isGroupRide ? (
              <>
                  {race.seriesId && (
                      <Link to={`/ride/${race.seriesId}`} className="font-medium text-cyan-400 hover:text-cyan-300 transition-colors">
                          ← Ride details
                      </Link>
                  )}
                  <span className="font-medium text-gray-300">Group Ride • {occurrenceLabel}</span>
              </>
          ) : showDropdown ? (
              <div className="relative group flex items-center">
                  <select
                      value={activeCategory.slug || activeCategory.id}
                      onChange={handleCategoryChange}
                      className="appearance-none bg-gray-700 hover:bg-gray-600 text-cyan-400 font-bold py-1 pl-3 pr-8 rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer transition-colors text-sm"
                      aria-label="Select Race Category"
                  >
                      {race.categories.map(cat => (
                          <option key={cat.id} value={cat.slug || cat.id} className="bg-gray-800 text-gray-200">
                              {cat.name}
                          </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                      <ChevronDownIcon className="h-4 w-4" />
                  </div>
              </div>
          ) : (
              <span className="font-medium text-gray-300">{activeCategory.name}</span>
          )}

          {!isGroupRide && (
              <>
                  <span className="text-gray-600 hidden sm:inline">|</span>
                  <span>{race.date}</span>
              </>
          )}
          <span className="text-gray-600 hidden md:inline">|</span>
          <span className="hidden md:inline">{isGroupRide ? 'Scheduled Roll' : 'Scheduled Start'}: {formatStartTime(activeCategory.scheduledStartTime || 0)}</span>
          <span className="text-gray-600 hidden md:inline">|</span>
          <span className="hidden md:inline">Actual Start: {formatActualStartTime(activeCategory.startTime)}</span>
          {!isGroupRide && (
              <>
                  <span className="text-gray-600 hidden lg:inline">|</span>
                  <span className="hidden lg:inline">Duration: {formatDuration(activeCategory.duration)}</span>
              </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
